/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { InteractiveComponents } from '../../../core/enums';
import { Log } from '../../../core/logging';
import type { TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import { setActiveInteractiveComponent } from '../../interactivelayer/interactiveLayer.reducers';
import {
    biaxialWidgetUpdateHorizontalPositionAction,
    biaxialWidgetUpdateVerticalPositionAction,
} from '../biaxialwidget.actions';
import { biaxialWidgetUpdateCaretVisible } from '../biaxialwidget.reducers';

const TAG = 'biaxialWidgetNavigation';

export const onKeyDownBiaxialWidget = () => (dispatch: Function, getState: TGetState) => {
    const { verticalItems, verticalPosition } = getState().biaxialWidget;
    if (!verticalItems.length) return;

    if (verticalPosition < verticalItems.length - 1) {
        dispatch(biaxialWidgetUpdateVerticalPositionAction(verticalPosition + 1));
    }
    Log.i(
        TAG,
        'onKeyDownBiaxialWidget action dispatched, verticalPosition: ',
        verticalPosition + 1,
    );
};

export const onKeyLeftBiaxialWidget = () => (dispatch: Function, getState: TGetState) => {
    const { horizontalItem, horizontalPosition } = getState().biaxialWidget;
    if (!horizontalItem.listItems.length) return;

    const newPosition =
        horizontalPosition > 0 ? horizontalPosition - 1 : horizontalItem.listItems.length - 1;
    dispatch(biaxialWidgetUpdateHorizontalPositionAction(newPosition));

    Log.i(TAG, 'onKeyLeftBiaxialWidget action dispatched, horizontalPosition: ', newPosition);
};

export const onKeyRightBiaxialWidget = () => (dispatch: Function, getState: TGetState) => {
    const { horizontalItem, horizontalPosition } = getState().biaxialWidget;
    if (!horizontalItem.listItems.length) return;

    const newPosition =
        horizontalPosition < horizontalItem.listItems.length - 1 ? horizontalPosition + 1 : 0;
    dispatch(biaxialWidgetUpdateHorizontalPositionAction(newPosition));

    Log.i(TAG, 'onKeyRightBiaxialWidget action dispatched, horizontalPosition: ', newPosition);
};

export const onKeyUpBiaxialWidget = () => (dispatch: Function, getState: TGetState) => {
    const { verticalItems, verticalPosition } = getState().biaxialWidget;
    if (!verticalItems.length) return;

    if (verticalPosition > 0) {
        dispatch(biaxialWidgetUpdateVerticalPositionAction(verticalPosition - 1));
    }
    Log.i(TAG, 'onKeyUpBiaxialWidget action dispatched, verticalPosition: ', verticalPosition - 1);
};

// Channel UP button: Move content up (navigate to earlier rows, 3 rows per page)
export const onKeyChannelUpBiaxialWidget = () => (dispatch: Function, getState: TGetState) => {
    const { verticalItems, verticalPosition } = getState().biaxialWidget;
    if (!verticalItems.length) return;

    const pageSize = 3;
    const newVerticalPosition = Math.max(verticalPosition - pageSize, 0);

    if (newVerticalPosition === verticalPosition) {
        Log.i(TAG, 'Already at top; cannot page up.');
        return;
    }

    dispatch(biaxialWidgetUpdateVerticalPositionAction(newVerticalPosition));
};

// Channel DOWN button: Move content down (navigate to later rows, 5 rows per page)
export const onKeyChannelDownBiaxialWidget = () => (dispatch: Function, getState: TGetState) => {
    const { verticalItems, verticalPosition } = getState().biaxialWidget;
    if (!verticalItems.length) return;

    const pageSize = 5;
    const maxIndex = Math.max(verticalItems.length - 1, 0);
    const newVerticalPosition = Math.min(verticalPosition + pageSize, maxIndex);

    if (newVerticalPosition === verticalPosition) {
        Log.i(TAG, 'Already at bottom; cannot page down.');
        return;
    }

    dispatch(biaxialWidgetUpdateVerticalPositionAction(newVerticalPosition));
};

export const onMouseOutBiaxialWidget = () => (dispatch: Function, getState: TGetState) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;
    if (
        value === InteractiveComponents.NORTH ||
        value === InteractiveComponents.SOUTH ||
        value === InteractiveComponents.EAST ||
        value === InteractiveComponents.WEST
    ) {
        dispatch(biaxialWidgetUpdateCaretVisible(false));
    }
};

export const onMouseOverBiaxialWidget = () => (dispatch: Function, getState: TGetState) => {
    const { value } = getState().inputFactory;
    if (value === InteractiveComponents.NORTH) {
        dispatch(setActiveInteractiveComponent(value));
        dispatch(biaxialWidgetUpdateCaretVisible(true));
    } else if (value === InteractiveComponents.SOUTH) {
        dispatch(setActiveInteractiveComponent(value));
        dispatch(biaxialWidgetUpdateCaretVisible(true));
    } else if (value === InteractiveComponents.EAST) {
        dispatch(setActiveInteractiveComponent(InteractiveComponents.EAST));
        dispatch(biaxialWidgetUpdateCaretVisible(true));
    } else if (value === InteractiveComponents.WEST) {
        dispatch(setActiveInteractiveComponent(InteractiveComponents.WEST));
        dispatch(biaxialWidgetUpdateCaretVisible(true));
    }
};
